import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EPlannStatus, HuntingDistrict, NetworkConstants, PointOfInterest } from 'diwimamodelweb/lib';
import { deepCopy, getTimeFirebaseTimestamp, isFirebaseTimestamp } from 'diwimamodelweb/lib/utils/obejct-diff';
import { contextIdsFromRights } from '../context-rights-picker/context-rights-picker.component';
import { ContextAccess, changedGeoFields, coordKey, isGeoLocked, isReadOnly, resolveContextAccess } from '../context-rights-picker/context-access';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { PoiMapEditorData, PointOfInterestForm, PoiSubForm } from '../model/form';
import { PoiService } from '../poi.service';
import { UserFeedbackService } from '../user-feedback.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SettingsService } from '../settings.service';
import { UserNameLookupService } from '../../../app/cockpit/work-orders/user-name-lookup.service'
import { firstValueFrom, skipWhile, take } from 'rxjs';

export interface NotificationChannelEntry {
  email: string;
  displayName?: string;
}

@Component({
  selector: 'app-poi-form',
  templateUrl: './poi-form.component.html',
  styleUrls: ['./poi-form.component.css', '../../app.component.scss']
})
export class PoiFormComponent implements OnInit {

  @Input() data: PointOfInterestForm
  @Input() formBuilder: FormBuilder
  @Input() userFeedbackService: UserFeedbackService
  @Input() translateService: TranslateService
  @Input() objectService: PoiService
  @Output() showSubForm: EventEmitter<PoiSubForm> = new EventEmitter()
  @Output() openMapEditor: EventEmitter<PoiMapEditorData> = new EventEmitter()

  isCreate = false
  isDeleted = false
  planningStatus = Object.values(EPlannStatus)
  poiForm: FormGroup
  oldPoi: PointOfInterest
  contentType:string = 'point'
  dropdownHuntingDistrict: HuntingDistrict[]
  dropdownSettingGeoObjectType: any[]
  dropdownSettingGeoObjectCategory:any[] = []

  grUpdateRight = false
  grDeleteRight = false

  notificationChannel: NotificationChannelEntry[] = []
  channelInputCtrl = new FormControl<string>('', { nonNullable: true })
  channelUserPickerCtrl = new FormControl<string[]>([], { nonNullable: true })

  contextAccess: ContextAccess = 'owner'
  get isViewOnly(): boolean { return isReadOnly(this.contextAccess) }
  get isGeoLocked(): boolean { return isGeoLocked(this.contextAccess) }
  get canSave(): boolean {
    return !this.isViewOnly && this.grUpdateRight && !this.poiForm?.invalid
  }
  get canDelete(): boolean {
    return this.grDeleteRight && this.contextAccess === 'owner'
  }

  private geoSnapshot: Record<string, any> = {}
  private static readonly GEO_FIELDS = [
    'huntingDistrictId', 'locationHeading', 'encodedContent', 'locationCoordinates',
  ]

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private settingsService: SettingsService,
    public userNameLookup: UserNameLookupService,
  ) { }

  async ngOnInit(): Promise<void> {
    const context = await firstValueFrom(this.settingsService.selectedUserContext$.pipe( skipWhile(el => !el), take(1) ))
    const rights = this.settingsService.rightsPercontext.get(context.identifier)

    this.grUpdateRight = !!rights?.[NetworkConstants.COLLECTION_POI]?.['U']
    this.grDeleteRight = !!rights?.[NetworkConstants.COLLECTION_POI]?.['D']
    if(this.isDeleted){
      this.data.onCancle()
    }
    if (!this.data.data) {
      this.isCreate = true
    }else {
      this.oldPoi = deepCopy(this.data.data, this.data.data.replacerFn)
      this.geoSnapshot = this.readGeoFields(this.data.data)
      if(this.data.data.contentType)
        this.contentType = this.data.data.contentType.toLowerCase()
      this.notificationChannel = Array.isArray((this.data.data as any)?.notificationChannel)
        ? (this.data.data as any).notificationChannel.filter((e: any) => !!e?.email)
        : []
    }
    this.dropdownHuntingDistrict = this.data.huntingDistricts
    this.dropdownSettingGeoObjectType = this.data.constextSettings.get(NetworkConstants.SETTING_GEO_TYPE).values
    this.dropdownSettingGeoObjectCategory = this.data.constextSettings.get(NetworkConstants.SETTING_GEO_CATEGORY).values

    switch(this.contentType){
      case 'point':{
        this.dropdownSettingGeoObjectType = this.data.constextSettings.get(NetworkConstants.SETTING_GEO_TYPE).values
        break;
      }

      case 'line':{
        this.dropdownSettingGeoObjectType = this.data.constextSettings.get(NetworkConstants.SETTING_GEO_LINE_TYPES).values
        break;
      }

      case 'polygon': {
        this.dropdownSettingGeoObjectType = this.data.constextSettings.get(NetworkConstants.SETTING_GEO_POLYGON_TYPES).values
        break;
      }

      default:{
        this.dropdownSettingGeoObjectType = this.data.constextSettings.get(NetworkConstants.SETTING_GEO_TYPE).values
        break;
      }
    }

    this.poiForm = this.formBuilder.group({
      tagid: [],
      geoObjectType: [, Validators.required],
      shortName: [, Validators.required],
      shortID: [],
      locationHeading: [, [Validators.min(0), Validators.max(360)]],
      huntingDistrict: [],
      category: [],
      planningStatus: [],
      nextTestDate: [],
      lastTestDate: [],
      testIntervalDays: [],
      contextIdRights: [{}]
    })
    if (this.data.data) {
      this.isCreate = false
      this.oldPoi=this.data.data
      this.initilizeWithData()
    }
    const subIds = this.settingsService.getSubcontextIds() // ?? (context as any)?.subContextIds

    this.contextAccess = resolveContextAccess(
      this.data.data as any,
      context?.identifier,
      subIds,
    )
    this.applyContextAccess()
    this.poiForm.valueChanges.subscribe( () => {
      const value = this.poiForm.getRawValue()
      const { tagid, geoObjectType, shortName, shortID, huntingDistrict, locationHeading, category, planningStatus, nextTestDate, lastTestDate, testIntervalDays } = value
      if (!this.isCreate){
        let anyObj = deepCopy(this.data.data)

        let nextDate = nextTestDate
        anyObj.category = category
        anyObj.shortName = shortName
        anyObj.shortID = shortID
        anyObj.huntingDistrictId= huntingDistrict?.identifier
        anyObj.huntingDistrictName= huntingDistrict?.name
        anyObj.huntingDistrictAdminsitrativeName= huntingDistrict?.administrativeName
        anyObj.type= geoObjectType?.identifier
        anyObj.typeTitle= geoObjectType?.title
        anyObj.locationHeading = locationHeading
        anyObj.planningStatus = planningStatus
        anyObj.nextTestDate = nextTestDate ? nextTestDate : nextDate
        anyObj.lastTestDate = lastTestDate
        anyObj.testIntervalDays = testIntervalDays
        this.data.data.setFromAny(anyObj)
      }
    })

    this.poiForm.get('contextIdRights')?.valueChanges.subscribe((rights: any) => {
      if (!this.data?.data) return
      const ids = contextIdsFromRights(rights)
      ;(this.data.data as any).contextIdRights = ids.length ? rights : undefined
      ;(this.data.data as any).contextIds      = ids.length ? ids : undefined
    })

    this.userNameLookup.ensureAllUsersLoaded()
  }

  get showNotificationChannel(): boolean {
    return !!this.poiForm?.get('geoObjectType')?.value?.displayOptions?.notificationChannel
  }

  private syncNotificationChannel(): void {
    if (!this.data?.data) return
    ;(this.data.data as any).notificationChannel = this.notificationChannel.length ? this.notificationChannel : undefined
  }

  get availableChannelUsers(): any[] {
    const taken = new Set(this.notificationChannel.map(e => e.email.toLowerCase()))
    return this.userNameLookup.currentUsers.filter((u: any) => {
      const email = (u?.email ?? '').toLowerCase()
      return !!email && !taken.has(email)
    })
  }

  onChannelUserPickerClosed(): void {
    const ids = this.channelUserPickerCtrl.value ?? []
    if (!ids.length) return
    ids.forEach(id => {
      const user = this.userNameLookup.currentUsers.find((u: any) => u.identifier === id)
      if (user) this.addUserChannel(user)
    })
    this.channelUserPickerCtrl.setValue([])
  }

  addUserChannel(user: any): void {
    const email = user?.email
    if (!email) return
    this.addChannelEntry({ email, displayName: this.userNameLookup.displayNameOf(user) || undefined })
  }

  get canAddTypedChannelEmail(): boolean {
    return this.isValidEmail(this.channelInputCtrl.value)
  }

  addTypedChannelEmail(): void {
    const email = this.channelInputCtrl.value?.trim() ?? ''
    if (!this.isValidEmail(email)) return
    this.addChannelEntry({ email })
  }

  onChannelInputEnter(event: Event): void {
    event.preventDefault()
    this.addTypedChannelEmail()
  }

  private addChannelEntry(entry: NotificationChannelEntry): void {
    const email = entry.email.trim()
    if (!email || this.notificationChannel.some(e => e.email.toLowerCase() === email.toLowerCase())) return

    this.notificationChannel = [
      ...this.notificationChannel,
      entry.displayName ? { email, displayName: entry.displayName } : { email },
    ]
    this.syncNotificationChannel()
    this.channelInputCtrl.setValue('')
  }

  removeNotificationChannel(index: number): void {
    this.notificationChannel = this.notificationChannel.filter((_, i) => i !== index)
    this.syncNotificationChannel()
  }

  private isValidEmail(value: string | null | undefined): boolean {
    return !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
  }

  get showPlanningStatus(): boolean {
    return !!this.poiForm?.get('geoObjectType')?.value?.displayOptions?.testInterval
  }

  private readGeoFields(poi: any): Record<string, any> {
    return {
      huntingDistrictId:   poi?.huntingDistrictId ?? null,
      locationHeading:     poi?.locationHeading ?? null,
      encodedContent:      poi?.encodedContent ?? null,
      locationCoordinates: coordKey(poi?.locationCoordinates),
    }
  }

  private applyContextAccess(): void {
    if (this.isViewOnly) {
      this.poiForm.disable({ emitEvent: false })
      return
    }
    if (this.isGeoLocked) {
      this.poiForm.get('huntingDistrict')?.disable({ emitEvent: false })
      this.poiForm.get('locationHeading')?.disable({ emitEvent: false })
    }
  }

  initilizeWithData() {
    const {tagid, shortName, shortID, locationHeading, category, planningStatus, testIntervalDays, lastTestDate, nextTestDay} = this.data.data
    const huntingDistrict = this.data.huntingDistricts?.find(el => el?.identifier == this.data.data.huntingDistrictId)
    const geoObjectType = this.dropdownSettingGeoObjectType?.find(el => el?.identifier == this.data.data.type)
    const geoObjectTypeLowerCase:any = this.dropdownSettingGeoObjectType?.find(el => el?.identifier?.toLowerCase() == this.data.data.type?.toLowerCase()) ?? {}
    geoObjectTypeLowerCase.identifier = geoObjectTypeLowerCase?.identifier ? geoObjectTypeLowerCase?.identifier.toLowerCase() : undefined
    this.poiForm.patchValue({
      shortID, shortName, tagid, huntingDistrict, geoObjectType:geoObjectTypeLowerCase, locationHeading, category, 
      planningStatus: planningStatus ? planningStatus : undefined,
      testIntervalDays,
      lastTestDate: isFirebaseTimestamp(lastTestDate) ? new Date(getTimeFirebaseTimestamp(lastTestDate)) : undefined,
      nextTestDay: isFirebaseTimestamp(nextTestDay) ? new Date(getTimeFirebaseTimestamp(nextTestDay)) : undefined,
      contextIdRights: (this.data.data as any)?.contextIdRights ?? {}
    })

  }

  get nextTestDate(): Date | null {
    const last: any = this.poiForm?.get('lastTestDate')?.value;
    const interval: number | null = this.poiForm?.get('testIntervalDays')?.value;
    if (!last || interval == null) return null;
    const base = last instanceof Date ? last : new Date(getTimeFirebaseTimestamp(last));
    if (!base) return null;
    const d = new Date(base);
    d.setDate(d.getDate() + interval);
    return d;
  }

  async onSubmit() {
    if (!this.canSave) return

    if (this.isGeoLocked) {
      const changed = changedGeoFields(
        this.geoSnapshot,
        this.readGeoFields(this.data.data),
        PoiFormComponent.GEO_FIELDS,
      )
      if (changed.length) {
        this.userFeedbackService.showSnackBar.emit('Geodaten können nicht geändert werden.')
        return
      }
    }

    this.data.onSubmit()
  }

  mapClicked() {
    // Geometry is never editable from a shared item
    if (this.isGeoLocked) {
      this.userFeedbackService.showSnackBar.emit(
        'Die Geometrie kann nur im Ursprungskontext geändert werden.',
      )
      return
    }

    // --- this should not be a feature right now 
    // if(this.data?.data?.contentType == 'POINT' )
    // this.showSubForm.emit(PoiSubForm.LOCATION)

    // this.openMapEditor.emit({
    //   data:this.data.data,
    //   form: this.poiForm.value,
    //   identifier: this.data.data.identifier,
    //   type: this.contentType
    // })

    this.router.navigate(['poiMap'], {relativeTo: this.route})

  }


  async onDelete(){
    if (!this.canDelete) return
    const title = await this.translateService.get('delete_title').toPromise()
    const message = await this.translateService.get('delete_message').toPromise()  
    this.userFeedbackService.loading.emit(false)
    this.userFeedbackService.showDialog.emit(
      {
        entryComponent: GenericDialogComponent,
        title: title,
        message: message,
        cancleCallback: async ()=> {
          const sbCancelDelete = await this.translateService.get('delete_canceled').toPromise() 
          this.userFeedbackService.showSnackBar.emit(sbCancelDelete)

        },
        positiveCallback: async ()=>{
          try{
            this.userFeedbackService.loading.emit(true)
            await this.data.onDelete()
            const sbSuccessDelete = await this.translateService.get('delete_completed').toPromise() 
            this.isDeleted = true 
            this.userFeedbackService.loading.emit(false)
            this.userFeedbackService.showSnackBar.emit(sbSuccessDelete)

          }catch(errorDelete){
            console.log(errorDelete)
            const sbErrorDelete = await this.translateService.get('delete_error').toPromise() 
            this.userFeedbackService.showSnackBar.emit(sbErrorDelete)
          }
        } 
      }
    )  
  }
}
